/**
 * The one colour a set of marks wears, as the colour most of them wear.
 *
 * Shared rather than duplicated, because two callers have to reach the *same* answer for the
 * feature they are both part of to hold: a region wears this colour while its group is open, and
 * the mark drawn for that group wears it once the group is closed. Closing a group must not change
 * what colour that part of the estate is, so the rule cannot be two implementations that happen to
 * agree - a tie-break changed on one side would break the promise silently, and only on data with
 * a tie in it.
 *
 * Modal rather than averaged, and rather than derived from the commonest type. Averaging two
 * categorical hues gives a third that names nothing - a region of applications and teams would
 * come out a muddy violet belonging to neither - and going by type would disagree with the marks
 * whenever conditional formatting has recoloured them, which is exactly when a reader is looking
 * for the group that is mostly red.
 *
 * Ties break on first appearance in the input, so a caller that passes its marks in a stable order
 * gets a stable colour on every rebuild.
 *
 * `null` where nothing had a colour to vote with, which the callers draw as structure ink: nothing
 * inside carries a component type, so there is no hue the region could honestly wear.
 */
export const toModalFill = (
  fills: readonly (string | null | undefined)[]
): string | null => {
  const counts = new Map<string, number>();
  for (const fill of fills) {
    if (fill === null || fill === undefined) continue;
    counts.set(fill, (counts.get(fill) ?? 0) + 1);
  }

  let best: string | null = null;
  let bestCount = 0;
  for (const [fill, count] of counts) {
    if (count > bestCount) {
      best = fill;
      bestCount = count;
    }
  }
  return best;
};
